import React, { useContext, useEffect, useMemo } from "react";
import { string, oneOf, array, bool, func, object } from "prop-types";
import { useTextDomainContext, useUser } from "@/app/providers";
import { SelectDropdown } from "..";
import { useTenantList } from "@/hooks";
import { getRoleTypeNameFromUser } from "@/utils";
import type { DropdownOption } from "@/types/Dropdown.types";

type TenantDropdownProps = {
  componentLabel?: string;
  defaultValue?: string | string[];
  cacheKey?: string;
  showAll?: boolean;
  required?: boolean;
  onChange?: (event: DropdownOption) => void;
  sx?: Record<string, unknown>;
  refetch?: boolean;
  refetched?: () => void;
};

export const TenantDropdown = (props: TenantDropdownProps) => {
  const {
    componentLabel: propsComponentLabel,
    defaultValue: propsDefaultValue,
    cacheKey,
    showAll,
    required,
    onChange,
    sx,
    refetch,
    refetched,
  } = props;

  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);
  const { user } = useUser();
  const roleType = getRoleTypeNameFromUser(user);

  const tenantListProps = useMemo(() => ({ activeOnly: true }), []);
  // Fetch tenants using the useTenantList hook with activeOnly filter
  const { tenantsList } = useTenantList(tenantListProps);

  //#region UseMemo
  const componentLabel = useMemo(() => {
    return propsComponentLabel || gettext("Corporate Group");
  }, [gettext, propsComponentLabel]);

  const defaultValue = useMemo(() => {
    return propsDefaultValue || "";
  }, [propsDefaultValue]);

  // Prepare options with "All" option for super users
  const options = useMemo<DropdownOption[]>(() => {
    if (roleType === "super" && tenantsList.length > 0) {
      const all = [{ label: gettext("All"), value: "all" }];
      return [
        ...all,
        ...tenantsList.map((tenant) => ({
          ...tenant,
          label: tenant.name || gettext("Corporate Group"),
          value: tenant.Id,
        })),
      ];
    }
    return tenantsList.map((tenant) => ({
      ...tenant,
      label: tenant.name || gettext("Corporate Group"),
      value: tenant.Id,
    }));
  }, [roleType, tenantsList, gettext]);
  //#endregion useMemo

  //#region UseEffect
  useEffect(() => {
    if (refetch && typeof refetched === "function") {
      // Notify parent that refetch is complete
      refetched();
    }
  }, [refetch, refetched, tenantsList]);
  //#endregion useEffect

  //#region Handlers
  const handleChange = (event: DropdownOption) => {
    if (typeof onChange === "function") {
      onChange(event);
    }
  };
  //#endregion Handlers

  return (
    <SelectDropdown
      options={options}
      componentLabel={componentLabel}
      defaultValue={defaultValue}
      cacheKey={cacheKey}
      showAll={showAll}
      required={required}
      onChange={handleChange}
      refetch={refetch || false}
      refetched={refetched}
      visualStyle="autoComplete"
      sx={{
        ...sx,
      }}
    />
  );
};

TenantDropdown.propTypes = {
  componentLabel: string,
  cacheKey: string,
  defaultValue: oneOf([string, array]),
  showAll: bool,
  required: bool,
  onChange: func,
  sx: object,
  refetch: bool,
  refetched: func,
};
